import { useTranslation } from "react-i18next";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import { GripVertical, RotateCcw, Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ColumnDefinition } from "@/hooks/useSalesReportColumns";

interface ColumnManagerProps {
  columns: ColumnDefinition[];
  columnOrder: string[];
  visibleColumns: string[];
  onToggleVisibility: (columnId: string) => void;
  onReorder: (newOrder: string[]) => void;
  onReset: () => void;
}

export function ColumnManager({
  columns,
  columnOrder,
  visibleColumns,
  onToggleVisibility,
  onReorder,
  onReset,
}: ColumnManagerProps) {
  const { t } = useTranslation();

  const orderedColumns = columnOrder
    .map(id => columns.find(col => col.id === id))
    .filter(Boolean) as ColumnDefinition[];

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(columnOrder);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    onReorder(items);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Settings2 className="h-4 w-4" />
          {t("salesReport.manageColumns")}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="end">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/50">
          <span className="font-medium text-sm">{t("salesReport.manageColumns")}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={onReset}
            className="h-8 px-2 text-xs gap-1"
          >
            <RotateCcw className="h-3 w-3" />
            {t("salesReport.resetColumns")}
          </Button>
        </div>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="columns">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="p-2 max-h-80 overflow-y-auto"
              >
                {orderedColumns.map((column, index) => (
                  <Draggable key={column.id} draggableId={column.id} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`flex items-center gap-2 p-2 rounded-md transition-colors ${
                          snapshot.isDragging ? "bg-accent shadow-md" : "hover:bg-muted"
                        }`}
                      >
                        <div
                          {...provided.dragHandleProps}
                          className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
                        >
                          <GripVertical className="h-4 w-4" />
                        </div>
                        <Checkbox
                          id={column.id}
                          checked={visibleColumns.includes(column.id)}
                          onCheckedChange={() => onToggleVisibility(column.id)}
                        />
                        <label
                          htmlFor={column.id}
                          className="flex-1 text-sm cursor-pointer select-none"
                        >
                          {t(column.labelKey)}
                        </label>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </PopoverContent>
    </Popover>
  );
}
